/**
 * Seat solver for the "Resume intent" chip — pure geometry, no DOM.
 *
 * A minimized intent dialog leaves exactly one control on screen: the chip that
 * brings it back. It used to be pinned by CSS to the bottom centre of the
 * viewport, which on every desktop page box is the middle card of the player's
 * hand (measured on a 1920x968 FHD page box: chip 884..1048 x 880..924 over
 * hand cards 754..1122 x 733..968). The owner's rule: ABOVE the hand on
 * desktop, or at the very least covering nothing and smoothly clickable.
 *
 * Candidates, in order of preference:
 *   above-hand         centred on the hand, one gap above its highest card —
 *                      the band between the player's slot row and the fan
 *   beside-hand-left   flush with the hand's top edge, one gap to its left
 *   beside-hand-right  the same on the right
 *   bottom-centre      the legacy seat, for a battle with no hand at all
 *
 * Every candidate is clamped into the safe box first (a notched phone hides
 * 59px behind each side of the page), then tested against every obstacle with
 * half a gap of clearance. The first clear candidate wins; when none is clear
 * the one covering the least area wins, so the chip is always somewhere
 * finite, inside the safe box, and as far out of the way as the box allows.
 *
 * `action-intent-restore.js` feeds this from the live battle furniture.
 */

/** @typedef {{ left: number, top: number, right: number, bottom: number }} Rect */
/** @typedef {{ seat: string, left: number, top: number, clear: boolean, overlap: number }} Seat */

/** @param {Rect[] | null | undefined} rects @returns {Rect | null} */
export function unionRect(rects) {
    if (!Array.isArray(rects) || rects.length === 0) return null;
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    for (const rect of rects) {
        left = Math.min(left, rect.left);
        top = Math.min(top, rect.top);
        right = Math.max(right, rect.right);
        bottom = Math.max(bottom, rect.bottom);
    }
    return { left, top, right, bottom };
}

/** @param {Rect | null | undefined} rect */
export function rectHasArea(rect) {
    return !!rect && rect.right - rect.left > 0 && rect.bottom - rect.top > 0;
}

/** True when `a` touches `b` grown by `clearance` on every side. */
export function rectsOverlap(a, b, clearance = 0) {
    return a.left < b.right + clearance
        && b.left - clearance < a.right
        && a.top < b.bottom + clearance
        && b.top - clearance < a.bottom;
}

function overlapArea(a, b, clearance) {
    const width = Math.min(a.right, b.right + clearance) - Math.max(a.left, b.left - clearance);
    const height = Math.min(a.bottom, b.bottom + clearance) - Math.max(a.top, b.top - clearance);
    return width > 0 && height > 0 ? width * height : 0;
}

/** Never off the near edge: a box wider than the safe area keeps its left/top. */
function clampInto(safe, left, top, width, height) {
    return {
        left: Math.max(safe.left, Math.min(safe.right - width, left)),
        top: Math.max(safe.top, Math.min(safe.bottom - height, top)),
    };
}

/**
 * @param {{
 *   safe: Rect,
 *   chip: { width: number, height: number },
 *   hand?: Rect[],
 *   handContainer?: Rect | null,
 *   obstacles?: Rect[],
 *   gap?: number,
 *   bottomClearance?: number,
 * }} input
 * @returns {Seat}
 */
export function solveRestoreSeat({
    safe, chip, hand = [], handContainer = null, obstacles = [], gap = 8, bottomClearance = 18,
}) {
    const { width, height } = chip;
    const anchor = unionRect(hand) || (rectHasArea(handContainer) ? handContainer : null);
    const blockers = obstacles.concat(hand);
    const candidates = [];
    if (anchor) {
        const centre = (anchor.left + anchor.right) / 2;
        candidates.push({ seat: 'above-hand', left: centre - width / 2, top: anchor.top - gap - height });
        candidates.push({ seat: 'beside-hand-left', left: anchor.left - gap - width, top: anchor.top });
        candidates.push({ seat: 'beside-hand-right', left: anchor.right + gap, top: anchor.top });
    }
    candidates.push({
        seat: 'bottom-centre',
        left: (safe.left + safe.right) / 2 - width / 2,
        top: safe.bottom - height - Math.max(gap, bottomClearance),
    });

    const clearance = gap / 2;
    let best = null;
    for (const candidate of candidates) {
        const { left, top } = clampInto(safe, candidate.left, candidate.top, width, height);
        const box = { left, top, right: left + width, bottom: top + height };
        const overlap = blockers.reduce((sum, blocker) => sum + overlapArea(box, blocker, clearance), 0);
        const seat = { seat: candidate.seat, left, top, clear: overlap === 0, overlap };
        if (seat.clear) return seat;
        if (!best || overlap < best.overlap) best = seat;
    }
    return /** @type {Seat} */ (best);
}
